import { useState, useEffect } from 'react';
import { Users, Plus, Pencil, UserPlus, Bot, X, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, getInitials } from '@/lib/utils';
import type { Team, TeamMember, User, AIAgent } from '@/types';
import { api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ContextMenu } from '@/components/common/ContextMenu';

const TEAM_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [form, setForm] = useState({ name: '', description: '', color: TEAM_COLORS[0] });
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allAgents, setAllAgents] = useState<AIAgent[]>([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [membersBusy, setMembersBusy] = useState(false);

  const selected = teams.find((t) => t.id === selectedId);
  const filteredTeams = teams.filter((team) => (
    !search || team.name.toLowerCase().includes(search.toLowerCase()) || (team.description ?? '').toLowerCase().includes(search.toLowerCase())
  ));

  const loadTeams = () => {
    setLoading(true);
    setError(null);
    api
      .get<{ success?: boolean; data?: Team[]; error?: string }>('/teams')
      .then((res) => {
        if (res.success === false || !Array.isArray(res.data)) throw new Error(res.error || 'Teams konnten nicht geladen werden.');
        setTeams(res.data);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Teams konnten nicht geladen werden.');
        setTeams([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTeams();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMembers([]);
    if (!selectedId) return;
    api.get<{ data?: TeamMember[] }>(`/teams/${selectedId}/members`)
      .then((res) => { if (!cancelled) setMembers(res.data ?? []); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Mitglieder konnten nicht geladen werden.'); });
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    api.get<{ data?: User[] }>('/users?is_active=true&limit=100').then((r) => setAllUsers(r.data ?? [])).catch(() => setAllUsers([]));
    api.get<{ data?: AIAgent[] }>('/ai/agents').then((r: any) => setAllAgents((r as { data?: AIAgent[] }).data ?? [])).catch(() => setAllAgents([]));
  }, []);

  const openCreate = () => {
    setEditingTeam(null);
    setForm({ name: '', description: '', color: TEAM_COLORS[0] });
    setDialogOpen(true);
  };

  const openEdit = (t: Team) => {
    setEditingTeam(t);
    setForm({ name: t.name, description: t.description ?? '', color: t.color });
    setDialogOpen(true);
  };

  const saveTeam = async () => {
    if (saving || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingTeam) {
        await api.patch(`/teams/${editingTeam.id}`, form);
      } else {
        await api.post('/teams', form);
      }
      setDialogOpen(false);
      loadTeams();
    } catch (e) {
      setError((e as Error).message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (memberId: string, memberType: 'user' | 'ai_agent', role: 'lead' | 'member') => {
    if (!selectedId || membersBusy) return;
    setMembersBusy(true);
    setError(null);
    try {
      await api.post(`/teams/${selectedId}/members`, { member_id: memberId, member_type: memberType, role });
      const res = await api.get<{ data?: TeamMember[] }>(`/teams/${selectedId}/members`);
      setMembers((res as { data?: TeamMember[] }).data ?? []);
    } catch (e) { setError(e instanceof Error ? e.message : 'Mitglied konnte nicht hinzugefügt werden.'); }
    finally { setMembersBusy(false); }
  };

  const removeMember = async (memberId: string, memberType: TeamMember['member_type']) => {
    if (!selectedId || membersBusy) return;
    setMembersBusy(true);
    setError(null);
    try {
      await api.delete(`/teams/${selectedId}/members/${memberId}?member_type=${memberType}`);
      setMembers((prev) => prev.filter((m) => m.member_id !== memberId || m.member_type !== memberType));
    } catch (e) { setError(e instanceof Error ? e.message : 'Mitglied konnte nicht entfernt werden.'); }
    finally { setMembersBusy(false); }
  };

  const updateMemberRole = async (memberId: string, memberType: TeamMember['member_type'], role: 'lead' | 'member') => {
    if (!selectedId || membersBusy) return;
    setMembersBusy(true);
    setError(null);
    try {
      await api.patch(`/teams/${selectedId}/members/${memberId}?member_type=${memberType}`, { role });
      setMembers((prev) => prev.map((m) => (m.member_id === memberId && m.member_type === memberType ? { ...m, role } : m)));
    } catch (e) { setError(e instanceof Error ? e.message : 'Rolle konnte nicht geändert werden.'); }
    finally { setMembersBusy(false); }
  };

  if (loading && teams.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Teams</h1>
          <p className="text-sm leading-relaxed text-muted">
            Mitglieder und Zuständigkeiten verwalten.
          </p>
        </div>
        <Button className="shrink-0" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Team anlegen
        </Button>
      </div>
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Teams suchen..."
        className="max-w-sm"
      />

      {error && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
          <Button variant="outline" size="sm" className="ml-3" onClick={loadTeams} disabled={loading}>Erneut laden</Button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredTeams.length === 0 ? (
          <Card className="col-span-full flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted" />
            <p className="mt-2 text-muted">{search ? 'Keine Teams gefunden.' : error ? 'Teams nicht verfügbar.' : 'Keine Teams vorhanden.'}</p>
            <Button className="mt-4" onClick={openCreate}>Team anlegen</Button>
          </Card>
        ) : (
          filteredTeams.map((t) => {
            const count = selectedId === t.id ? members.length : (t.members?.length ?? 0);
            const memberList = selectedId === t.id ? members : (t.members ?? []);
            return (
              <ContextMenu
                key={t.id}
                actions={[
                  { label: 'Öffnen', onClick: () => { if (!membersBusy) setSelectedId(selectedId === t.id ? null : t.id); } },
                  { label: 'Bearbeiten', onClick: () => openEdit(t) },
                ]}
              >
                <Card
                  className={cn(
                    'cursor-pointer border-border bg-card shadow-sm transition-shadow hover:shadow',
                    selectedId === t.id && 'ring-2 ring-border'
                  )}
                  onClick={() => { if (!membersBusy) setSelectedId(selectedId === t.id ? null : t.id); }}
                >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: t.color }} />
                      <div>
                        <CardTitle className="text-base">
                          <button
                            type="button"
                            aria-expanded={selectedId === t.id}
                            disabled={membersBusy}
                            className="rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={(event) => { event.stopPropagation(); setSelectedId(selectedId === t.id ? null : t.id); }}
                          >
                            {t.name}
                          </button>
                        </CardTitle>
                        <p className="text-xs text-muted line-clamp-1">{t.description || '—'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" aria-label={`${t.name} bearbeiten`} onClick={(e) => { e.stopPropagation(); openEdit(t); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {memberList.slice(0, 4).map((m) => (
                        <Avatar key={m.id} className="h-7 w-7 border-2 border-white dark:border-border">
                          {m.member_type === 'ai_agent' ? (
                            <AvatarFallback className="bg-card-hover text-foreground text-xs dark:bg-primary-hover dark:text-muted">
                              <Bot className="h-3 w-3" />
                            </AvatarFallback>
                          ) : (
                            <>
                              <AvatarImage src={m.user?.avatar_url} />
                              <AvatarFallback className="text-xs">{getInitials(m.user?.full_name ?? '?')}</AvatarFallback>
                            </>
                          )}
                        </Avatar>
                      ))}
                    </div>
                    <span className="text-sm text-muted">{count} Mitglieder</span>
                  </div>
                </CardContent>
                </Card>
              </ContextMenu>
            );
          })
        )}
      </div>

      {selected && (
        <Card className="border-border bg-card shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: selected.color }} />
              <CardTitle>{selected.name}</CardTitle>
            </div>
            <p className="text-sm text-muted">{selected.description || '—'}</p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-medium">Mitglieder</h3>
              <div className="flex gap-2">
                <Select value="" disabled={membersBusy} onValueChange={(v) => {
                  const [type, id] = v.split(':');
                  if (id) addMember(id, type as 'user' | 'ai_agent', 'member');
                }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Mitglied hinzufügen" />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers.filter((u) => !members.some((m) => m.member_type === 'user' && m.member_id === u.id)).map((u) => (
                      <SelectItem key={u.id} value={`user:${u.id}`}>{u.full_name} (User)</SelectItem>
                    ))}
                    {allAgents.filter((a) => !members.some((m) => m.member_type === 'ai_agent' && m.member_id === a.id)).map((a) => (
                      <SelectItem key={a.id} value={`ai_agent:${a.id}`}>{a.name} (AI)</SelectItem>
                    ))}
                    {allUsers.length === 0 && allAgents.length === 0 && (
                      <SelectItem value="_" disabled>Keine weiteren verfügbar</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <ScrollArea className="h-[240px] rounded-md border border-border">
              <ul className="p-2 space-y-2">
                {members.length === 0 ? (
                  <li className="py-6 text-center text-muted">Keine Mitglieder. Fügen Sie User oder AI-Agenten hinzu.</li>
                ) : (
                  members.map((m) => (
                    <li key={m.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                      <div className="flex items-center gap-3">
                        {m.member_type === 'ai_agent' ? (
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-card-hover text-foreground dark:bg-primary-hover dark:text-muted">
                              <Bot className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={m.user?.avatar_url} />
                            <AvatarFallback>{getInitials(m.user?.full_name ?? '?')}</AvatarFallback>
                          </Avatar>
                        )}
                        <div>
                          <p className="font-medium text-foreground">
                            {m.member_type === 'user' ? m.user?.full_name : m.agent?.name ?? 'Unbekannt'}
                          </p>
                          <p className="text-xs text-muted">{m.member_type === 'user' ? m.user?.email : 'AI-Agent'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select disabled={membersBusy} value={m.role} onValueChange={(v) => updateMemberRole(m.member_id, m.member_type, v as 'lead' | 'member')}>
                          <SelectTrigger aria-label={`Rolle für ${m.user?.full_name || m.agent?.name || 'Mitglied'}`} className="w-[100px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lead">Lead</SelectItem>
                            <SelectItem value="member">Mitglied</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" disabled={membersBusy} onClick={() => removeMember(m.member_id, m.member_type)}>Entfernen</Button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!saving) setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Team bearbeiten' : 'Neues Team'}</DialogTitle>
          </DialogHeader>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="team-name">Name</Label>
              <AIInput id="team-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="z. B. Marketing Kern" aiFieldLabel="Team-Name" aiContext="Marketing-Team" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="team-desc">Beschreibung</Label>
              <AIInput id="team-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} onValueChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Optionale Beschreibung" aiFieldLabel="Team-Beschreibung" aiContext={`Team: ${form.name || 'neues Team'}`} />
            </div>
            <div className="grid gap-2">
              <Label>Farbe</Label>
              <div className="flex gap-2">
                {TEAM_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Teamfarbe ${c}`}
                    aria-pressed={form.color === c}
                    className={cn('h-8 w-8 rounded-full border-2 transition', form.color === c ? 'border-border scale-110' : 'border-transparent hover:scale-105')}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={() => setDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button disabled={saving || !form.name.trim()} onClick={saveTeam}>
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
